/**
 * Sharing service — manage collection view access between users
 */

import { supabase } from './supabase';

export const sharingService = {
  /**
   * Returns the list of users the current user has shared their collection with.
   * @returns {Promise<Array<{share_id, viewer_id, viewer_email, created_at}>>}
   */
  async getMyViewers() {
    const { data, error } = await supabase.rpc('get_my_viewers');
    if (error) throw error;
    return data || [];
  },

  /**
   * Returns the list of collections shared with the current user.
   * @returns {Promise<Array<{share_id, owner_id, owner_email, created_at}>>}
   */
  async getSharedWithMe() {
    const { data, error } = await supabase.rpc('get_shared_with_me');
    if (error) throw error;
    return data || [];
  },

  /**
   * Grants view access to another user by their email address.
   * @param {Object} params
   * @param {string} params.email - Email of the user to share with
   * @param {boolean} params.shareBottles - Share bottle collection
   * @param {boolean} params.shareHunt - Share hunt list
   * @returns {Promise<Object>} The created share record
   */
  async shareWithUser({ email, shareBottles = true, shareHunt = true }) {
    const { data: users, error: findError } = await supabase.rpc('find_user_by_email', {
      lookup_email: email.trim()
    });
    if (findError) throw findError;
    if (!users || users.length === 0) {
      throw new Error('No account found with that email address');
    }

    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('collection_shares')
      .insert({
        owner_id: user.id,
        viewer_id: users[0].user_id,
        share_bottles: shareBottles,
        share_hunt: shareHunt
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('You have already shared your collection with this user');
      }
      throw error;
    }

    // Send share notification email (best-effort — don't fail the share if email fails)
    try {
      await supabase.functions.invoke('send-share-notification', {
        body: { viewerEmail: email.trim(), shareBottles, shareHunt },
      });
    } catch (emailErr) {
      console.warn('Share notification email failed (non-fatal):', emailErr);
    }

    return data;
  },

  /**
   * Revokes a previously granted share.
   * @param {string} shareId - UUID of the share record
   * @returns {Promise<void>}
   */
  async revokeShare(shareId) {
    const { error } = await supabase
      .from('collection_shares')
      .delete()
      .eq('id', shareId);
    if (error) throw error;
  }
};
